import { Button, Tooltip, Typography } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import InfoIcon from '@material-ui/icons/Info'
import abbreviate from 'abbreviate-name'
import {
  DatePicker,
  Filter,
  List,
  Snackbar,
  TextField,
  useFilter,
  useListRepository
} from 'elentari'
import moment from 'moment'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { Field } from 'react-final-form'
import { CustomAutocomplete } from '../../components'
import { getListActions } from '../../lib/crudAuthorization'
import {
  formatAnaliseDocumentos,
  formatSolicitacaoFilterInitialValues
} from '../../utils/Formatter'
import {
  fonteLiberacaoOptions,
  solicitacoesAnaliseDeDocumentosOptions,
  solicitacoesStatusOptions
} from '../../utils/Options'
import { parseCpf } from '../../utils/Parse'
import { getUsuarioId } from '../../utils/Utils'
import CentroCustoAutocomplete from '../CentrosCusto/CentroCustoAutocomplete'
import EmpresaSelect from '../Empresa/EmpresaSelect'
import MercadoriaAutocomplete from '../Mercadorias/MercadoriaAutocomplete'
import UnidadeNegocioAutocomplete from '../UnidadeNegocio/UnidadeNegocioAutocomplete'
import { useAbility } from '../Usuarios'
import solicitacoesRepository from './SolicitacoesRepository'
import { BadgeFonteLiberacao, BadgeStatus, StatusEnum } from './components'

const useStyles = makeStyles(theme => ({
  header: {
    padding: theme.spacing(2)
  },
  info: {
    display: 'flex',
    justifyContent: 'space-between'
  }
}))

const SolicitacoesEstatisticas = forwardRef((props, ref) => {
  const [quantidadeFila, setQuantidadeFila] = useState(0)
  const [quantidadeFilaAnalista, setQuantidadeFilaAnalista] = useState(0)
  const [
    quantidadeFilaExclusivaAnalista,
    setQuantidadeFilaExclusivaAnalista
  ] = useState(0)

  const fetchData = async () => {
    const or = [
      { status: StatusEnum.ABERTO },
      { status: StatusEnum.REABERTO },
      { status: StatusEnum.STANDBY }
    ]
    const where = {
      or
    }
    const whereAnalista = {
      and: [{ or }, { responsavelId: getUsuarioId() }]
    }
    const whereExclusivaAnalista2 = {
      and: [{ or }, { exclusivaAnalista2: true }]
    }
    const [
      response,
      responseAnalista,
      responseExclusivaAnalista2
    ] = await Promise.all([
      solicitacoesRepository.countPure(where),
      solicitacoesRepository.countPureAnalista(whereAnalista),
      solicitacoesRepository.countPureExclusivaAnalista2(
        whereExclusivaAnalista2
      )
    ])
    if (response.ok) {
      setQuantidadeFila(response.data.count)
    }
    if (responseAnalista.ok) {
      setQuantidadeFilaAnalista(responseAnalista.data.count)
    }
    if (responseExclusivaAnalista2.ok) {
      setQuantidadeFilaExclusivaAnalista(responseExclusivaAnalista2.data.count)
    }
  }

  useImperativeHandle(ref, () => ({
    update: () => {
      fetchData()
    }
  }))

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => fetchData(), 1000 * 30)
    return () => clearInterval(interval)
  }, [])

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      spacing={2}
    >
      <Grid item sm={4} xs={12}>
        Quantidade na fila: {quantidadeFila}
        {quantidadeFila > 0 && (
          <Tooltip
            placement="top"
            title="Algumas solicitações podem estar exclusivas para analistas"
          >
            <InfoIcon fontSize="small" />
          </Tooltip>
        )}
      </Grid>
      <Grid item sm={4} xs={12}>
        Minhas solicitações na fila: {quantidadeFilaAnalista}
      </Grid>
      <Grid item sm={4} xs={12}>
        Solicitações exclusiva assistente: {quantidadeFilaExclusivaAnalista}
      </Grid>
    </Grid>
  )
})

const SolicitacoesAnalistaFilter = () => {
  const classes = useStyles()
  const { onClear, onSubmit, initialValues } = useFilter({})

  const detailedFilter = () => (
    <Grid container spacing={2}>
      <Grid item md={6} xs={12}>
        <Field
          fullWidth
          component={DatePicker}
          label="Data Criação"
          name="createdAt"
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <Field
          fullWidth
          component={DatePicker}
          label="Data Término"
          name="dataTermino"
        />
      </Grid>
      <Grid item md={3} xs={12}>
        <EmpresaSelect />
      </Grid>
      <Grid item md={3} xs={12}>
        <Field
          fullWidth
          name="unidadeNegocioId"
          label="Unidades de negócio"
          component={UnidadeNegocioAutocomplete}
        />
      </Grid>
      <Grid item md={3} xs={12}>
        <Field
          fullWidth
          name="centroCustoId"
          label="Centro de custo"
          component={CentroCustoAutocomplete}
        />
      </Grid>
      <Grid item md={3} xs={12}>
        <Field
          fullWidth
          options={solicitacoesAnaliseDeDocumentosOptions}
          name="analiseDocumentos"
          label="Análise de Documentos"
          component={CustomAutocomplete}
        />
      </Grid>
    </Grid>
  )

  return (
    <Filter
      onClear={onClear}
      onSubmit={onSubmit}
      detailedFilter={detailedFilter()}
      initialValues={formatSolicitacaoFilterInitialValues(initialValues)}
      labels={{
        detailedFilter: 'Busca avançada',
        simpleFilter: 'Busca simples',
        find: 'Buscar',
        clear: 'Limpar'
      }}
    >
      <Grid spacing={3} container>
        <Grid className={classes.info} item xs={12}>
          <Typography variant="h6">Filtros</Typography>
          <Tooltip title="Os filtros irão considerar registros dos últimos 7 dias">
            <InfoIcon />
          </Tooltip>
        </Grid>
        <Grid item md={4} xs={12}>
          <Field
            fullWidth
            name="motorista"
            label="Motorista"
            component={TextField}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Field
            fullWidth
            name="cpf"
            label="CPF Motorista"
            parse={parseCpf}
            component={TextField}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Field
            fullWidth
            name="mercadoriaId"
            label="Mercadoria"
            component={MercadoriaAutocomplete}
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <Field
            fullWidth
            name="nomeEmbarcador"
            label="Embarcador"
            component={TextField}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Field
            fullWidth
            options={solicitacoesStatusOptions}
            name="status"
            label="Status"
            component={CustomAutocomplete}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <Field
            fullWidth
            options={fonteLiberacaoOptions}
            name="fonteLiberacao"
            label="Fonte de Liberação"
            component={CustomAutocomplete}
          />
        </Grid>
        <Grid item md={1} xs={12}>
          <Field fullWidth name="placa" label="Placa" component={TextField} />
        </Grid>
      </Grid>
    </Filter>
  )
}

const SolicitacoesAnalista = ({ history, ...props }) => {
  const [ability] = useAbility()
  const classes = useStyles()
  const [mensagem, setMensagem] = useState()
  const refEstatisticas = useRef(null)

  const listHook = useListRepository({
    repository: solicitacoesRepository,
    path: 'solicitacoes',
    query: [['analista', true]]
  })

  useEffect(() => {
    const interval = setInterval(() => listHook.fetchList(), 1000 * 30)
    return () => clearInterval(interval)
  }, [listHook])

  const listOptions = {
    fields: {
      id: {
        label: 'Id'
      },
      motorista: {
        label: 'Motorista',
        format: value => (value ? abbreviate(value.nome) : ''),
        noOrder: true
      },
      createdat: {
        label: 'Data Criação',
        format: value => moment(value).format('DD/MM/YYYY HH:mm:ss')
      },
      status: {
        label: 'Status',
        format: (value, solicitacao) => (
          <BadgeStatus
            status={value}
            fonteLiberacao={solicitacao.fonteLiberacao}
            cenario={solicitacao.cenario}
          />
        )
      },
      dataTermino: {
        label: 'Data de Término',
        format: data => (data ? moment(data).format('DD/MM/YYYY HH:mm') : '-')
      },
      mercadoria: {
        label: 'Mercadoria',
        format: value => value.descricao
      },
      nomeEmbarcador: {
        label: 'Embarcador',
        format: value => (value ? abbreviate(value) : ''),
        noOrder: true
      },
      centroCusto: {
        label: 'Centro de Custo',
        format: centroCusto => (centroCusto ? centroCusto.descricao : '-'),
        noOrder: true
      },
      analiseDocumentos: {
        label: 'Tipo',
        format: formatAnaliseDocumentos
      },
      fonteLiberacao: {
        label: 'Fonte de Liberação',
        format: fonteLiberacao => (
          <BadgeFonteLiberacao fonteLiberacao={fonteLiberacao} />
        ),
        align: 'center'
      }
    }
  }

  const handleProxima = async () => {
    const response = await solicitacoesRepository.proxima()
    if (response.ok) {
      listHook.fetchList()
      refEstatisticas.current.update()
    } else {
      setMensagem(response.data.error.message)
    }
  }

  const actions = getListActions(ability, listHook, 'solicitacoesAnalista')

  return (
    <>
      <SolicitacoesAnalistaFilter />
      <Paper className={classes.header}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item lg={11} sm={8} xs={12}>
            <SolicitacoesEstatisticas ref={refEstatisticas} />
          </Grid>
          <Grid item lg={1} sm={4} xs={12}>
            <Grid container direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleProxima}
              >
                Próxima
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <List
        {...props}
        {...listHook}
        {...actions}
        stickyHeader
        adjustToScreenHeight
        onClickNew={null}
        onClickEdit={null}
        onClickView={null}
        onClickRow={item =>
          history.push(
            `/solicitacoesAnalista/${item.id}${history.location.search}`
          )
        }
        hideDownloadIcon
        listOptions={listOptions}
      />
      <Snackbar message={mensagem} onClose={() => setMensagem('')} />
    </>
  )
}

export default SolicitacoesAnalista
